/**
 * IntentionsGrid Component
 * Grid of intention cards with alternating wide/narrow layout matching Figma design.
 * Desktop: 4 columns per row, alternating [wide, narrow, wide, narrow] / [narrow, wide, narrow, wide]
 * Mobile: 2 columns with uniform sizing
 */

import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { useIsMobile } from "@/hooks/useIsMobile"

interface Intention {
  id: string
  name: string
  image?: string
  color?: string
  isSolid?: boolean
}

interface IntentionsGridProps {
  intentions: Intention[]
  onShowMore?: () => void
}

// Rounded corner patterns matching Figma design
// Wide cards: top-left + bottom-right (slash pattern)
// Narrow cards row 1: top-right + bottom-left (backslash pattern)
// Narrow cards row 2: top-right + bottom-right (right-side pattern)
function getCornerClasses(rowIndex: number, isWide: boolean): string {
  if (isWide) {
    return "rounded-tl-[50px] rounded-br-[50px]"
  }
  if (rowIndex % 2 === 0) {
    return "rounded-tr-[50px] rounded-bl-[50px]"
  }
  return "rounded-tr-[50px] rounded-br-[50px]"
}

const MAX_ROWS = 2
const MAX_MOBILE_ITEMS = 6

export function IntentionsGrid({ intentions, onShowMore }: IntentionsGridProps) {
  const isMobile = useIsMobile()
  // Chunk intentions into rows of 4, limit to 2 rows
  const allRows: Intention[][] = []
  for (let i = 0; i < intentions.length; i += 4) {
    allRows.push(intentions.slice(i, i + 4))
  }

  const rows = allRows.slice(0, MAX_ROWS)
  const mobileIntentions = intentions.slice(0, MAX_MOBILE_ITEMS)

  return (
    <section className="py-12.5 pt-0 md:pt-15 max-w-360 mx-auto px-5 md:px-17.5">
      <div className="flex items-start justify-between mb-4 md:mb-7.5">
        <div className="flex flex-col gap-1.25">
          <h2 className="section-heading text-xl md:text-2xl text-primary uppercase leading-normal md:leading-7.5 font-semibold opacity-90">
            Begin with Intention
          </h2>
          {isMobile ? (
            <p className="text-primary text-[13px] leading-5.5 opacity-90">
              Start with your intention and Rituality will help you find the best coaches and
              rituals.
            </p>
          ) : (
            <p className="text-primary text-sm leading-6.25 opacity-90">
              Start with your intention and Rituality will help you find the best coaches and
              rituals to help you reach your goal.
            </p>
          )}
        </div>
        {onShowMore && !isMobile && (
          <button
            type="button"
            onClick={onShowMore}
            className="text-sm text-primary font-semibold transition-colors flex items-center gap-1.5 mt-1 shrink-0 cursor-pointer"
          >
            Show more
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Intentions Grid - alternating wide/narrow layout */}
      <div className="flex flex-col gap-5">
        {rows.map((row, rowIndex) => {
          // Even rows: wide-narrow-wide-narrow
          // Odd rows: narrow-wide-narrow-wide
          const isEvenRow = rowIndex % 2 === 0

          return (
            <div
              key={rowIndex}
              className="hidden xl:grid gap-5"
              style={{
                gridTemplateColumns: isEvenRow ? "39fr 23fr 39fr 23fr" : "23fr 39fr 23fr 39fr",
              }}
            >
              {row.map((intention, colIndex) => {
                const isWide = isEvenRow ? colIndex % 2 === 0 : colIndex % 2 === 1
                const corners = getCornerClasses(rowIndex, isWide)

                return (
                  <Link
                    key={intention.id}
                    to={`/learn-and-practice/intention/${intention.id}`}
                    className={`relative h-[280px] overflow-hidden cursor-pointer group block ${corners}`}
                  >
                    {intention.image ? (
                      <img
                        src={intention.image}
                        alt={intention.name}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div
                        className="absolute inset-0"
                        style={{ backgroundColor: intention.color || "#5A5B3F" }}
                      />
                    )}

                    {intention.image && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    )}

                    <div className="absolute bottom-5 left-5 z-10">
                      <span className="font-semibold text-xl text-white uppercase">
                        {intention.name}
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )
        })}

        {/* Mobile: mixed full-width + 2-col layout */}
        <div className="grid grid-cols-2 gap-4 md:gap-5 xl:hidden">
          {mobileIntentions.map((intention, index) => {
            const isFull = index % 3 === 0
            return (
              <Link
                key={intention.id}
                to={`/learn-and-practice/intention/${intention.id}`}
                className={`relative overflow-hidden cursor-pointer group block ${
                  isFull ? "col-span-2 h-[180px]" : "h-[180px]"
                } ${
                  isFull
                    ? "rounded-tl-[40px] rounded-br-[40px]"
                    : index % 2 === 1
                      ? "rounded-tl-[40px] rounded-br-[40px]"
                      : "rounded-tr-[40px] rounded-bl-[40px]"
                }`}
              >
                {intention.image ? (
                  <img
                    src={intention.image}
                    alt={intention.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: intention.color || "#5A5B3F" }}
                  />
                )}

                {intention.image && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                )}

                <div className="absolute bottom-3 left-3 z-10">
                  <span className="font-semibold text-[15px] leading-normal md:text-base text-white uppercase">
                    {intention.name}
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
      {onShowMore && isMobile && (
        <button
          type="button"
          onClick={onShowMore}
          className="text-sm text-primary font-semibold transition-colors flex items-center gap-1.5 shrink-0 mt-5 cursor-pointer"
        >
          Show more
          <ArrowRight className="w-5 h-5" />
        </button>
      )}
    </section>
  )
}
